<div class="accounts-wrap mx-auto max-w-7xl px-4 py-8 antialiased sm:px-6 lg:px-8">
  <div class="mb-6 space-y-3 font-mono text-xs font-black uppercase tracking-wider">
    @if (errorMessage) {
      <div
        class="flex items-center gap-3 rounded-2xl border border-danger/20 bg-danger-soft px-4 py-3.5 text-danger shadow-sm animate-fadeIn">
        <div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-danger-soft text-danger">
          <i class="fa-solid fa-circle-exclamation text-[11px]"></i>
        </div>
        <span class="font-sans font-bold normal-case text-text-primary">{{ errorMessage }}</span>
      </div>
    }

    @if (isLoading) {
      <div
        class="flex items-center gap-3 rounded-2xl border border-primary/20 bg-primary-soft px-4 py-3.5 text-primary shadow-sm animate-pulse">
        <div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
          <i class="fa-solid fa-spinner fa-spin text-[11px]"></i>
        </div>
        <span class="text-[10px] font-black tracking-widest text-text-secondary">
          {{ 'accountsInformation.states.loading' | translate }}
        </span>
      </div>
    }
  </div>

  <div class="grid grid-cols-1 items-start gap-6 lg:grid-cols-12 lg:gap-8">
    <main class="space-y-6 lg:col-span-8">
      <app-account-profile-card [profile]="profile" [stats]="stats"></app-account-profile-card>

      <app-account-section-card [title]="'accountsInformation.sections.profileDetails' | translate" iconClass="fa-solid fa-address-card">
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          @for (detail of accountDetails; track detail.label) {
            <div class="rounded-2xl border border-border bg-surface-elevated p-4">
              <div class="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-text-muted">
                <i [class]="detail.iconClass + ' text-primary'"></i>
                {{ detail.label | translate }}
              </div>
              <div class="break-words text-sm font-bold text-text-primary">
                {{ detail.value.startsWith('accountsInformation.') ? (detail.value | translate) : detail.value }}
              </div>
            </div>
          } @empty {
            <div class="rounded-2xl border border-dashed border-border bg-surface-elevated p-5 text-sm text-text-muted">
              {{ 'accountsInformation.empty.profileDetails' | translate }}
            </div>
          }
        </div>
      </app-account-section-card>

      <app-account-section-card [title]="'accountsInformation.sections.documents' | translate" iconClass="fa-solid fa-file-shield">
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          @for (document of accountDocuments; track document.label) {
            <a [href]="document.url" target="_blank" rel="noopener"
              class="group overflow-hidden rounded-2xl border border-border bg-surface-elevated transition-all duration-200 hover:border-border-primary hover:bg-surface-hover">
              <div class="aspect-video bg-surface-hover">
                <img [src]="document.url" [alt]="document.label" class="h-full w-full object-cover" loading="lazy" />
              </div>
              <div class="flex items-center justify-between gap-3 p-4">
                <div>
                  <div class="text-[10px] font-black uppercase tracking-widest text-text-muted">
                    {{ document.label | translate }}
                  </div>
                  <div class="mt-1 text-sm font-bold text-text-primary">{{ 'accountsInformation.actions.openDocument' | translate }}</div>
                </div>
                <div
                  class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border-primary bg-primary-soft text-primary">
                  <i [class]="document.iconClass + ' text-xs'"></i>
                </div>
              </div>
            </a>
          } @empty {
            <div class="rounded-2xl border border-dashed border-border bg-surface-elevated p-5 text-sm text-text-muted">
              {{ 'accountsInformation.empty.documents' | translate }}
            </div>
          }
        </div>
      </app-account-section-card>

      <app-account-achievements-section [items]="achievements"></app-account-achievements-section>

      <app-account-section-card [title]="'accountsInformation.sections.structuredLists' | translate" iconClass="fa-solid fa-list-check">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div class="rounded-2xl border border-border bg-surface-elevated p-4">
            <h3 class="mb-3 text-xs font-black uppercase tracking-widest text-text-primary">{{ 'accountsInformation.lists.specializations' | translate }}</h3>
            <div class="flex flex-wrap gap-2">
              @for (item of specializations; track item) {
                <span class="rounded-full border border-primary/20 bg-primary-soft px-3 py-1 text-xs font-bold text-primary">
                  {{ item }}
                </span>
              } @empty {
                <span class="text-xs text-text-muted">{{ 'accountsInformation.empty.specializations' | translate }}</span>
              }
            </div>
          </div>

          <div class="rounded-2xl border border-border bg-surface-elevated p-4">
            <h3 class="mb-3 text-xs font-black uppercase tracking-widest text-text-primary">{{ 'accountsInformation.lists.certificates' | translate }}</h3>
            <ul class="space-y-2">
              @for (item of certificatesAndLicenses; track item) {
                <li class="flex items-start gap-2 text-xs font-semibold text-text-secondary">
                  <i class="fa-solid fa-certificate mt-0.5 text-primary"></i>
                  <span>{{ item }}</span>
                </li>
              } @empty {
                <li class="text-xs text-text-muted">{{ 'accountsInformation.empty.certificates' | translate }}</li>
              }
            </ul>
          </div>

          <div class="rounded-2xl border border-border bg-surface-elevated p-4">
            <h3 class="mb-3 text-xs font-black uppercase tracking-widest text-text-primary">{{ 'accountsInformation.lists.achievements' | translate }}</h3>
            <ul class="space-y-2">
              @for (item of achievementsAndAwards; track item) {
                <li class="flex items-start gap-2 text-xs font-semibold text-text-secondary">
                  <i class="fa-solid fa-trophy mt-0.5 text-primary"></i>
                  <span>{{ item }}</span>
                </li>
              } @empty {
                <li class="text-xs text-text-muted">{{ 'accountsInformation.empty.achievements' | translate }}</li>
              }
            </ul>
          </div>
        </div>
      </app-account-section-card>

      <app-account-section-card [title]="'accountsInformation.sections.properties' | translate" iconClass="fa-solid fa-building">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          @for (property of accountProperties; track property.id) {
            <article
              class="overflow-hidden rounded-2xl border border-border bg-surface-elevated transition-all duration-200 hover:border-border-primary hover:bg-surface-hover">
              <div class="relative aspect-[16/10] bg-surface-hover">
                @if (property.thumbnailUrl) {
                  <img [src]="property.thumbnailUrl" [alt]="property.title" class="h-full w-full object-cover" loading="lazy" />
                } @else {
                  <div class="flex h-full w-full items-center justify-center text-text-muted">
                    <i class="fa-solid fa-building text-3xl"></i>
                  </div>
                }

                <div class="absolute left-3 top-3 flex flex-wrap gap-2">
                  @if (property.isFeatured) {
                    <span class="rounded-full border border-primary/20 bg-primary-soft px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-primary">
                      {{ 'accountsInformation.badges.featured' | translate }}
                    </span>
                  }
                  @if (property.furnished) {
                    <span class="rounded-full border border-success/20 bg-success-soft px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-success">
                      {{ 'accountsInformation.badges.furnished' | translate }}
                    </span>
                  }
                </div>
              </div>

              <div class="space-y-4 p-4">
                <div>
                  <h3 class="line-clamp-2 text-sm font-black text-text-primary">
                    {{ property.title.startsWith('accountsInformation.') ? (property.title | translate) : property.title }}
                  </h3>
                  <p class="mt-1 text-xs font-semibold text-text-muted">{{ property.locationText || ('accountsInformation.empty.location' | translate) }}</p>
                </div>

                <div class="flex flex-wrap gap-2">
                  <span class="property-chip">{{ property.type.startsWith('accountsInformation.') ? (property.type | translate) : property.type }}</span>
                  <span class="property-chip">
                    {{ property.purpose ? (property.purpose.startsWith('accountsInformation.') ? (property.purpose | translate) : property.purpose) : ('accountsInformation.labels.purpose' | translate) }}
                  </span>
                  <span class="property-chip">
                    {{ property.priceType ? (property.priceType.startsWith('accountsInformation.') ? (property.priceType | translate) : property.priceType) : ('accountsInformation.labels.price' | translate) }}
                  </span>
                </div>

                <div class="grid grid-cols-3 gap-2 text-center">
                  <div class="rounded-xl border border-border bg-surface p-2">
                    <div class="text-sm font-black text-text-primary">{{ property.area }}</div>
                    <div class="text-[10px] font-bold uppercase tracking-wider text-text-muted">{{ 'accountsInformation.property.area' | translate }}</div>
                  </div>
                  <div class="rounded-xl border border-border bg-surface p-2">
                    <div class="text-sm font-black text-text-primary">{{ property.bedrooms }}</div>
                    <div class="text-[10px] font-bold uppercase tracking-wider text-text-muted">{{ 'accountsInformation.property.beds' | translate }}</div>
                  </div>
                  <div class="rounded-xl border border-border bg-surface p-2">
                    <div class="text-sm font-black text-text-primary">{{ property.bathrooms }}</div>
                    <div class="text-[10px] font-bold uppercase tracking-wider text-text-muted">{{ 'accountsInformation.property.baths' | translate }}</div>
                  </div>
                </div>

                <div class="flex items-center justify-between gap-3 border-t border-border pt-3">
                  <div>
                    <div class="text-[10px] font-black uppercase tracking-widest text-text-muted">{{ 'accountsInformation.property.displayPrice' | translate }}</div>
                    <div class="text-base font-black text-primary">
                      {{ property.displayPrice | number }} {{ property.currency }}
                    </div>
                  </div>
                  <div class="text-right text-[10px] font-bold uppercase tracking-wider text-text-muted">
                    {{ property.createdAt ? (property.createdAt | date:'yyyy-MM-dd') : ('accountsInformation.empty.date' | translate) }}
                  </div>
                </div>
              </div>
            </article>
          } @empty {
            <div class="rounded-2xl border border-dashed border-border bg-surface-elevated p-8 text-center text-sm text-text-muted md:col-span-2">
              {{ 'accountsInformation.empty.properties' | translate }}
            </div>
          }
        </div>
      </app-account-section-card>

      <app-account-gallery-section [categories]="galleryCategories"></app-account-gallery-section>
      <app-account-reviews-section [reviews]="reviews"></app-account-reviews-section>
    </main>

    <aside class="lg:col-span-4">
      <div class="sticky top-28 space-y-5">
        <app-account-contact-card [responseTime]="responseTime" [phoneNumber]="contactPhoneNumber"
          [email]="contactEmail"></app-account-contact-card>

        <app-account-trust-safety-card [checks]="trustChecks"></app-account-trust-safety-card>

        <div class="rounded-3xl border border-border bg-surface p-5 shadow-subtle">
          <div class="mb-4 flex items-center justify-between gap-3">
            <div>
              <h3 class="text-xs font-black uppercase tracking-widest text-text-primary">{{ 'accountsInformation.sections.responseSummary' | translate }}</h3>
              <p class="mt-1 text-xs text-text-muted">{{ 'accountsInformation.sections.responseSummarySubtitle' | translate }}</p>
            </div>
            <div
              class="flex h-10 w-10 items-center justify-center rounded-xl border border-border-primary bg-primary-soft text-primary">
              <i class="fa-solid fa-code-branch text-xs"></i>
            </div>
          </div>

          <div class="space-y-3 text-xs">
            <div class="flex items-center justify-between gap-3 border-b border-border pb-2">
              <span class="text-text-muted">{{ 'accountsInformation.stats.properties' | translate }}</span>
              <span class="font-black text-text-primary">{{ rawProfile?.propertiesCount ?? accountProperties.length }}</span>
            </div>
            <div class="flex items-center justify-between gap-3 border-b border-border pb-2">
              <span class="text-text-muted">{{ 'accountsInformation.stats.successRate' | translate }}</span>
              <span class="font-black text-success">{{ rawProfile?.successRate ?? 0 }}%</span>
            </div>
            <div class="flex items-center justify-between gap-3 border-b border-border pb-2">
              <span class="text-text-muted">{{ 'accountsInformation.stats.deals' | translate }}</span>
              <span class="font-black text-text-primary">{{ rawProfile?.successfulDeals ?? 0 }}</span>
            </div>
            <div class="flex items-center justify-between gap-3">
              <span class="text-text-muted">{{ 'accountsInformation.details.profileVisibility' | translate }}</span>
              <span class="font-black text-primary">{{ profileVisibilityText | translate }}</span>
            </div>
          </div>
        </div>

        <div class="p-1">
          <button type="button"
            class="group inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl border border-border bg-surface-elevated px-4 py-3 font-mono text-xs font-black uppercase tracking-widest text-text-muted shadow-sm transition-all duration-200 hover:border-danger/30 hover:bg-danger-soft hover:text-danger active:scale-[0.98]">
            <i class="fa-solid fa-triangle-exclamation text-[11px] text-text-muted transition-colors group-hover:text-danger"></i>
            <span>{{ 'accountsInformation.actions.reportProfile' | translate }}</span>
          </button>
        </div>
      </div>
    </aside>
  </div>
</div>
